import { useLayoutContext } from '../Contexts/LayoutContext';
import { useOrderTypeContext } from '../Contexts/OrderTypeContext';
import { useStoreContext } from '../Contexts/StoreContext';
import DOMPurify from 'dompurify';
import { compareEquals, generateOperatingHoursText, orderTypeAsSlug } from '../Utils/Helpers';
import StoreService from '../Services/StoreService';
import { format } from 'date-fns';
import { CASHIER_STATUS_OPEN } from '../Utils/Constants/Checkout';
import { useLoginContext } from '../Contexts/LoginContext';
import { useCheckoutContext } from '../Contexts/CheckoutContext';
import CartService from '../Services/CartService';
import { useCartContext } from '../Contexts/CartContext';
import { ERROR_CART_NOT_FOUND } from '../Utils/Constants/Cart';
import CompanyService from '../Services/CompanyService';
import { useAddProductContext } from '../Contexts/AddProductContext';
import { ORDER_TYPE_DELIVERY } from '../Utils/Constants/Service';
import { TokenManager } from '../Services/TokenManager';
import { useTranslation } from 'react-i18next';

export const useStoreHook = () => {

  const { t } = useTranslation();
  const { company, setCompany, setLoadingCompany, setCompanyPages, setMainLoading, setMainLoadingIcon, setMainLoadingIconError, setMainLoadingTitle, setMainLoadingMessage, setMainLoadingCloseButton,setMainLoadingIconNoSearchMap, setMainLoadingIconStoreClosed, setMainLoadingAutoClose } = useLayoutContext();
  const { currentStore, checkStoreOpen, setCurrentStore, setLoadingStore, setCashier, setRequiredTable, setTable, setTables, currentAddress } = useStoreContext();
  const { orderType, getOrderTypeIdByName } = useOrderTypeContext();
  const { setGeolocation } = useLoginContext();
  const { setShowPixCode } = useCheckoutContext();
  const { setCurrentCart, setLoadingCurrentCart, setCartDisplay } = useCartContext();
  const { setShowAddProduct, setProduct, setCategory} = useAddProductContext();

  const loadCompanyInfo = async () => {
    try {
        setLoadingCompany(true);
        const companyService = new CompanyService(orderType);
        const companyInfo = await companyService.info();

        if (companyInfo) {
            setCompany(companyInfo)
            setCompanyPages(companyInfo.company_pages)
            applyColorsToTailwind(companyInfo.config.styles)
        }
    } 
    catch (error) {}
    finally {setLoadingCompany(false)}
  }

  const applyColorsToTailwind = (colors: any) => {
      const root = document.documentElement;
      Object.keys(colors).forEach(key => {
          root.style.setProperty(`--color-${key}`, colors[key]);
      });
  }

  const loadStoreInfo = async (storeId: number | string, enableLoading: boolean = true) => {
    setLoadingStore(enableLoading);
    try {
        const storeSevice = new StoreService(orderType);
        const payload = {store_id: storeId}
        const getStoreInfo = await storeSevice.info(payload);
        if (!compareEquals(getStoreInfo, currentStore)) {
          setCurrentStore(getStoreInfo);
        }
        return getStoreInfo;
    } catch (error) {
      setTimeout(() => {
        setMainLoading(true);
        setMainLoadingIcon(false);
        setMainLoadingIconError(false);
        setMainLoadingIconNoSearchMap(true);
        setMainLoadingCloseButton(true);
        const title = DOMPurify.sanitize(t('layout.store_list.no_store_id_found', {storeId: storeId}));
        setMainLoadingTitle(t('layout.store_list.no_stores_found'));
        setMainLoadingMessage(title);
        setCurrentStore(null);
      }, 500);
      throw error;
    } finally {
      setLoadingStore(false);
    }
  }

  const createGuestToken = async (current_store_id: number, order_type_id: number) => {
    try {
      const cartService = new CartService(orderType);
      const payload = {
        store_id: current_store_id,
        order_type: order_type_id,
      }
      const getGuestToken = await cartService.guestToken(payload);
      if (getGuestToken.token) {
        TokenManager.setGuestToken(getGuestToken.token);
      }
      return getGuestToken;
    } catch (error) {
      throw error;
    }
  }

  interface GetActiveCartParams {
    store_id: number;
    order_type_id: number;
    order_type: string;
  }
  const getActiveCart = async ({
    store_id,
    order_type_id,
    order_type
  }: GetActiveCartParams) => {
    try {
      setLoadingCurrentCart(true)
      const cartService = new CartService(order_type);
      const payload = {        
        store_id,
        order_type: order_type_id,

        //* Utilizado para calcular o shipping_value
        ...(currentAddress && (order_type === ORDER_TYPE_DELIVERY)
          ? {
              zipcode: currentAddress.zipcode,
              number: currentAddress.number,
              lat: currentAddress.lat,
              lng: currentAddress.lng
            }
          : {})
      };

      const getGuestCart = await cartService.getActiveCart(payload);
      if (getGuestCart) {
        setCurrentCart(getGuestCart);
        setLoadingCurrentCart(false);
        setCartDisplay(null); //* Resetando o display do carrinho do store_counter
      }
      return getGuestCart;
    } catch (error: any) {
      setLoadingCurrentCart(false);

      if (error.response?.data?.error === ERROR_CART_NOT_FOUND) {
        setCurrentCart(null);
      }
      throw error
    }
  }

  const storeIsOpen = ({ enableLoading = true, onlyOpeningHours = true } = {}) => {
    const orderTypeId = Number(getOrderTypeIdByName(orderType));
    if (!orderType || isNaN(orderTypeId)) {
      console.warn('Tipo de pedido inválido.');
      return false;
    }
    if (!currentStore || !Array.isArray(currentStore.store_workdays)) {
      console.warn('Informações da loja inválidas.');
      return false;
    }
    if (!checkStoreOpen(orderTypeId)) {
      setTimeout(() => {
        setMainLoading(true);
        setMainLoadingIcon(false);
        setMainLoadingIconStoreClosed(true);
        setMainLoadingCloseButton(true);

        const today = new Date().getDay();
        const storeWorkdays = Array.isArray(currentStore?.store_workdays)
          ? currentStore.store_workdays.filter((cw: any) =>
              cw.service_type === orderTypeId &&
              cw.active &&
              typeof cw.weekday === 'number' &&
              (onlyOpeningHours ? cw.weekday === today : cw.weekday >= 0 && cw.weekday <= 6) && // 👈 aqui está o ajuste
              typeof cw.operation_from === 'string' &&
              typeof cw.operation_to === 'string'
            )
          : [];

        const hasWorkdays = storeWorkdays.length > 0;
        const operatingHoursMessage = DOMPurify.sanitize(generateOperatingHoursText(storeWorkdays, onlyOpeningHours));

        if (onlyOpeningHours) {
          setMainLoadingTitle(
            t('layout.store_closed_title') +
            (hasWorkdays ? `<br />${t('layout.store_closed_message_hour', {hour: operatingHoursMessage})}` : '')
          );
          setMainLoadingMessage('');
        } else {
          setMainLoadingTitle(t('layout.store_closed_check_hour_title'));
          setMainLoadingMessage((hasWorkdays ? operatingHoursMessage: ''));
        }
        loadStoreInfo(currentStore.id, enableLoading).catch(err => console.error('Erro ao recarregar loja:', err));
      }, 250);
      return false;
    }
    return true;
  };

  const noOpenCashier = () => {
      //* Removendo informações do usuário/pagamento
      setGeolocation(null);
      setTable(null);
      setShowPixCode(false);
  }

  const getCashier = async () => {
      const payload = {
          store_id: currentStore.id,
          user: true,
          sort: 'ASC',
          date_from: format(new Date(), 'yyyy-MM-dd'),
          date_to: format(new Date(), 'yyyy-MM-dd'),
          status: CASHIER_STATUS_OPEN
      }

      try {
          const storeService = new StoreService(orderType);
          const getCashiers = await storeService.cashiers(payload);

          if (getCashiers && Array.isArray(getCashiers) && getCashiers.length > 0) {
              setCashier(getCashiers[0]);
              return getCashiers[0];
          } else {
            setCashier(null);
            return null;
          }
        } catch (error) {
          console.error('Error cashiers:', error);
          return null;
        }
  }

  const getLinkToHome = (): string => {
    if (orderType && currentStore && currentStore.slug) {
        return `/${orderTypeAsSlug(orderType)}/${currentStore.slug}`;
    } else {
        return '/';
    }
  }

  const loadTables = async () => {
    try {
        const storeSevice = new StoreService(orderType);
        const payload = {
            store_id: currentStore.id,
            tables: true
        }
        const tables = await storeSevice.tables(payload).catch(() => {});
        if (tables) {
            setTables(tables)
            return tables;
        }
    } catch (error) {
      throw error;
    }
  }

  const updateCartTable = async (cart_id: number, table_id: number | null) => {
    const payload = {
      cart_id,
      order_type: Number(getOrderTypeIdByName(orderType)),
      store_id: currentStore.id,
      store_table_id: table_id,
    }
    try {
        const cartService = new CartService(orderType);
        const response = await cartService.updateCartTable(cart_id, payload);
        if (response) {
          setRequiredTable(false);
        }
        return response;
    } catch (error) {
      throw error;
    }
  }

  const storeNotFound = (address:string = '') => {
    setMainLoadingIcon(false);
    setMainLoadingIconNoSearchMap(true);
    setMainLoading(true);
    setMainLoadingTitle(t('layout.store_list.no_stores_found_locale_title'));
    if (address) {
      setMainLoadingMessage(address);
    } else {
      setMainLoadingMessage('');
    }
    setMainLoadingCloseButton(true);
  }

  const findProductBySlug = (categories: any[], slug: string) => {
    const searchRecursive = (cats: any[]): { product: any; category: any } | null => {
      for (const cat of cats) {
        // Search in current category
        const product = cat.products?.find((p: any) => p.slug === slug);
        if (product) {
          return { product, category: cat };
        }
        // Search in children
        if (cat.children?.length > 0) {
          const result = searchRecursive(cat.children);
          if (result) return result;
        }
      }
      return null;
    };

    const found = searchRecursive(categories);

    if (found) {
      setProduct(found.product);
      setCategory(found.category);
    } else {
      setMainLoading(true);
      setMainLoadingIcon(false);
      setMainLoadingIconError(true);
      setMainLoadingTitle(t('products.no_product_found_slug_title'));
      setMainLoadingMessage(t('products.no_product_found_slug_message'));
      setMainLoadingCloseButton(true);
      document.title = currentStore.name || company.name;
      window.history.replaceState({}, "", getLinkToHome());
    }
  }

  const openProductToAdd = (product: any, category: any) => {
    setProduct(product);
    setCategory(category);
    setShowAddProduct(true);
  }

  const orderTypeDisabledOnStore = () => {
    setMainLoading(true);
    setMainLoadingIcon(false);
    setMainLoadingIconError(true);
    setMainLoadingAutoClose(true);
    setMainLoadingTitle(t('layout.order_type_not_active_on_store_title'));
    setMainLoadingMessage(t('layout.order_type_not_active_on_store_message'));
    setMainLoadingCloseButton(true);
  }

  return {
    loadCompanyInfo,
    loadStoreInfo,
    storeIsOpen,
    getCashier,
    noOpenCashier,
    createGuestToken,
    getActiveCart,
    getLinkToHome,
    loadTables,
    updateCartTable,
    storeNotFound,
    findProductBySlug,
    openProductToAdd,
    orderTypeDisabledOnStore
  };
}


